import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  Check,
  Copy,
  ExternalLink,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  RotateCw,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { store, useStore } from "@/lib/store";
import { cn } from "@/lib/utils";
import { ConnectPlatformCard, ViewSection, relativeTime } from "@/views/shared";
import { serverStatusBadge } from "@/views/servers/ServersView";

// Local minimal confirm — deliberately not imported from views/projects so the
// servers surface stays self-contained. Uses a plain Button instead of
// AlertDialogAction because Radix auto-closes on Action click and async
// confirms need the dialog open until the store call resolves.
function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  actionLabel,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  actionLabel: string;
  pending?: boolean;
  onConfirm: () => void | Promise<void>;
}) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!pending) onOpenChange(next);
      }}
    >
      <AlertDialogContent className="glass glass-border max-w-[calc(100vw-2rem)] rounded-xl sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={pending} onClick={() => void onConfirm()}>
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {actionLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function InlineError({ message }: { message: string | undefined }) {
  if (!message) return null;
  return <p className="mt-2 text-xs text-destructive">{message}</p>;
}

function SectionCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("glass-border rounded-xl bg-card/40 p-3", className)}>{children}</div>
  );
}

export function ServerDetailView({ id }: { id: string }) {
  const state = useStore();
  const { platform, errors } = state;
  const detail = platform.projectDetail[id];
  const listItem = platform.mcpServers.items.find((p) => p.id === id);
  const logs = platform.serverLogs[id];
  const secrets = platform.serverSecrets[id];
  const pending = platform.pendingAction;

  const [logsRefreshing, setLogsRefreshing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const [rotateOpen, setRotateOpen] = useState(false);
  // Component state only, so the rotated token vanishes on navigation.
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // A token minted for one server must never linger when the route id flips.
    setNewToken(null);
    setCopied(false);
    void store.loadProjectDetail(id);
    void store.loadServerLogs(id);
    void store.loadServerSecrets(id);
  }, [id]);

  useEffect(() => {
    return () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
  }, []);

  if (!platform.available) return <ConnectPlatformCard />;

  const name = detail?.name ?? listItem?.name;
  const status = detail?.status ?? listItem?.status;
  const endpoint = detail?.productionUrl ?? detail?.url ?? listItem?.url ?? null;

  const trimmedKey = newKey.trim();
  const addPending = trimmedKey !== "" && pending === `secret-set:${id}:${trimmedKey}`;
  const deletePending = deleteKey !== null && pending === `secret-delete:${id}:${deleteKey}`;
  const rotatePending = pending === `rotate:${id}`;

  const secretActionErrors = Object.entries(errors).filter(
    ([key, message]) =>
      message !== undefined &&
      (key.startsWith(`platform:secret-set:${id}:`) ||
        key.startsWith(`platform:secret-delete:${id}:`)),
  );

  async function refreshLogs() {
    setLogsRefreshing(true);
    try {
      await store.loadServerLogs(id);
    } finally {
      setLogsRefreshing(false);
    }
  }

  async function submitSecret() {
    if (!trimmedKey || !newValue) return;
    try {
      await store.setServerSecret(id, trimmedKey, newValue);
      setAddOpen(false);
      setNewKey("");
      setNewValue("");
    } catch {
      // Failure already lands in the errors map; keep the dialog open so the
      // user can retry without retyping the value.
    }
  }

  async function confirmDelete() {
    if (deleteKey === null) return;
    try {
      await store.deleteServerSecret(id, deleteKey);
    } catch {
      // Error surfaces inline below the secrets list.
    } finally {
      setDeleteKey(null);
    }
  }

  async function confirmRotate() {
    try {
      const token = await store.rotateServerToken(id);
      setNewToken(token);
      setCopied(false);
    } catch {
      // Error surfaces inline in the access token section.
    } finally {
      setRotateOpen(false);
    }
  }

  function copyToken() {
    if (!newToken) return;
    void navigator.clipboard.writeText(newToken).then(() => {
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="flex flex-col p-4">
      <div className="mb-4">
        <button
          type="button"
          onClick={() => store.navigate({ name: "servers" })}
          className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          MCP servers
        </button>
        {name === undefined ? (
          <Skeleton className="h-6 w-48" />
        ) : (
          <div className="flex items-center justify-between gap-2">
            <h1 className="truncate text-lg font-semibold">{name}</h1>
            {serverStatusBadge(status)}
          </div>
        )}
        {endpoint && (
          <a
            href={endpoint}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex min-w-0 max-w-full items-center gap-1 font-mono text-xs text-primary hover:underline"
          >
            <span className="truncate">{endpoint}</span>
            <ExternalLink className="h-3 w-3 shrink-0" />
          </a>
        )}
        <InlineError message={errors[`platform:detail:${id}`]} />
      </div>

      <ViewSection
        title="Logs"
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void refreshLogs()}
            disabled={logsRefreshing}
            className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          >
            {logsRefreshing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Refresh
          </Button>
        }
      >
        {logs ? (
          <>
            <ScrollArea className="max-h-72 rounded-lg">
              <pre className="whitespace-pre-wrap break-words rounded-lg border border-border bg-card p-3 font-mono text-xs">
                {logs.text}
              </pre>
            </ScrollArea>
            <p className="mt-1 text-right text-[10px] text-muted-foreground">
              fetched {relativeTime(new Date(logs.fetchedAt).toISOString())}
            </p>
          </>
        ) : (
          <SectionCard>
            <p className="text-sm text-muted-foreground">No logs yet</p>
          </SectionCard>
        )}
        <InlineError message={errors[`platform:logs:${id}`]} />
      </ViewSection>

      <ViewSection
        title="Secrets"
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAddOpen(true)}
            className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          >
            <Plus className="h-3.5 w-3.5" />
            Add secret
          </Button>
        }
      >
        <SectionCard>
          {secrets === undefined ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-full rounded-md" />
              <Skeleton className="h-8 w-full rounded-md" />
            </div>
          ) : secrets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No secrets set</p>
          ) : (
            <ul className="flex flex-col divide-y divide-border/60">
              {secrets.map((secret) => {
                const rowPending = pending === `secret-delete:${id}:${secret.key}`;
                return (
                  <li key={secret.key} className="flex items-center justify-between gap-2 py-1.5">
                    <span className="flex min-w-0 items-center gap-2">
                      <KeyRound className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate font-mono text-xs">{secret.key}</span>
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                      aria-label={`Delete secret ${secret.key}`}
                      disabled={rowPending}
                      onClick={() => setDeleteKey(secret.key)}
                    >
                      {rowPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>
        <InlineError message={errors[`platform:secrets:${id}`]} />
        {secretActionErrors.map(([key, message]) => (
          <InlineError key={key} message={message} />
        ))}
      </ViewSection>

      <ViewSection title="Access token">
        <SectionCard>
          <p className="text-sm text-muted-foreground">
            Rotating mints a new bearer token for this server. Existing clients using the old token
            stop working immediately.
          </p>
          <Button
            variant="heroOutline"
            size="sm"
            className="mt-3 gap-1.5"
            disabled={rotatePending}
            onClick={() => setRotateOpen(true)}
          >
            {rotatePending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RotateCw className="h-3.5 w-3.5" />
            )}
            Rotate token
          </Button>

          {newToken && (
            <div className="mt-3">
              <div className="flex items-start gap-2 rounded-lg border border-border bg-card p-3">
                <code className="min-w-0 flex-1 break-all font-mono text-xs">{newToken}</code>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-muted-foreground"
                  aria-label="Copy token"
                  onClick={copyToken}
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <p className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                <TriangleAlert className="h-3 w-3 shrink-0" />
                Shown only once — copy it now. It disappears when you leave this page.
              </p>
            </div>
          )}
        </SectionCard>
        <InlineError message={errors[`platform:rotate:${id}`]} />
      </ViewSection>

      <Dialog
        open={addOpen}
        onOpenChange={(next) => {
          if (!addPending) setAddOpen(next);
        }}
      >
        <DialogContent className="glass glass-border max-w-[calc(100vw-2rem)] rounded-xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add secret</DialogTitle>
            <DialogDescription>
              The value is sent write-only to the server and never shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="secret-key" className="text-xs text-muted-foreground">
                Key
              </Label>
              <Input
                id="secret-key"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="API_KEY"
                autoComplete="off"
                spellCheck={false}
                className="font-mono"
                disabled={addPending}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="secret-value" className="text-xs text-muted-foreground">
                Value
              </Label>
              <Input
                id="secret-value"
                type="password"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="font-mono"
                disabled={addPending}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={addPending} onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={addPending || !trimmedKey || !newValue}
              onClick={() => void submitSecret()}
            >
              {addPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save secret
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteKey !== null}
        onOpenChange={(next) => {
          if (!next) setDeleteKey(null);
        }}
        title="Delete secret"
        description={`The server loses access to ${deleteKey ?? ""} on its next deploy. This cannot be undone.`}
        actionLabel="Delete"
        pending={deletePending}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={rotateOpen}
        onOpenChange={setRotateOpen}
        title="Rotate access token"
        description="A new token is minted and the current one is revoked immediately. Every client still using the old token will stop working until it is updated."
        actionLabel="Rotate"
        pending={rotatePending}
        onConfirm={confirmRotate}
      />
    </div>
  );
}
